/**
 * Snapshot of the server-rendered #root, taken at boot before hydration.
 *
 * Why this exists: RootErrorBoundary must never trade prerendered content for
 * an error screen. 2026-08-27: a post-deploy re-render storm made Googlebot's
 * WRS drop chunk fetches on 93 locale pages; the boundary replaced fully
 * prerendered HTML with the "§ ERROR" fallback, Google indexed that DOM, and
 * pages started collapsing into cross-locale duplicates (/fi/use-cases/parking
 * got /pt-pt/use-cases/parking as its Google-chosen canonical).
 *
 * "This page came prerendered" CANNOT be inferred from #root having children.
 * The .htaccess SPA fallback serves the prerendered HOMEPAGE for every
 * unmatched route (/dashboard, /projects, ...), so #root is non-empty there
 * too, holding the WRONG page's content. The reliable signal is the canonical
 * link the SSG stamped into <head>: it names the exact route the HTML was
 * rendered for. A page counts as prerendered-for-this-path only when both:
 *   1. #root has element children at boot, and
 *   2. the canonical's pathname equals location.pathname (normalized).
 * App routes served through the fallback fail (2), because the homepage
 * canonical is "/", so they keep the reload-budget -> error-screen behavior.
 */

interface PrerenderSnapshot {
  html: string;
  /** normalized location.pathname at capture time */
  pathname: string;
  forThisPath: boolean;
  /** outerHTML of every Helmet-managed head tag (`[data-rh]`, title excluded) as the server sent it */
  headTags: string[];
  title: string;
}

let snapshot: PrerenderSnapshot | null = null;

/**
 * /bg/cctv-layout/, /bg/cctv-layout.html and /bg/cctv-layout/index.html are
 * the same document under the server's rewrite rules, so they normalize to
 * /bg/cctv-layout before comparison.
 */
export function normalizePathname(pathname: string): string {
  let p = pathname;
  if (p.endsWith('/index.html')) p = p.slice(0, -'/index.html'.length);
  else if (p.endsWith('.html')) p = p.slice(0, -'.html'.length);
  while (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p === '' ? '/' : p;
}

/** Must run before hydration, while #root still holds the server's bytes. */
export function capturePrerenderSnapshot(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  try {
    const root = document.getElementById('root');
    if (!root || root.childElementCount === 0) return;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    let forThisPath = false;
    if (canonical?.href) {
      try {
        forThisPath =
          normalizePathname(new URL(canonical.href, window.location.origin).pathname) ===
          normalizePathname(window.location.pathname);
      } catch {
        /* malformed canonical -> treat as not prerendered for this path */
      }
    }
    snapshot = {
      html: root.innerHTML,
      pathname: normalizePathname(window.location.pathname),
      forThisPath,
      headTags: Array.from(document.head.querySelectorAll('[data-rh]'))
        .filter((el) => el.tagName !== 'TITLE')
        .map((el) => el.outerHTML),
      title: document.title,
    };
  } catch {
    /* never let boot fail over a recovery snapshot */
  }
}

/**
 * True while the document the server sent for THIS url is still a valid
 * fallback: it was prerendered for exactly this path and the SPA has not
 * client-navigated away from it since boot.
 */
export function hasPrerenderForCurrentPath(): boolean {
  return (
    !!snapshot &&
    snapshot.forThisPath &&
    typeof window !== 'undefined' &&
    normalizePathname(window.location.pathname) === snapshot.pathname
  );
}

/**
 * True once, for the page that mounts over server HTML already on screen: the
 * boot loader (scripts/ssg-postprocess.mjs) sets data-html-first-paint when it
 * holds app.js until the first frame. Entrance animations use it to leave that
 * page as it was painted instead of hiding it and playing it in again.
 */
export function takeHtmlFirstPaint(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  if (!root.hasAttribute('data-html-first-paint') || !hasPrerenderForCurrentPath()) return false;
  root.removeAttribute('data-html-first-paint');
  return true;
}

/** The captured server HTML, only when it is valid for the current path. */
export function getPrerenderHtml(): string | null {
  return hasPrerenderForCurrentPath() && snapshot ? snapshot.html : null;
}

/**
 * The server's page body without what RootLayout renders beside the route
 * (the two notification regions and the JSON-LD scripts), for showing in
 * the route's place while its chunk loads. Null when there is none.
 */
export function getPrerenderPageHtml(): string | null {
  const html = getPrerenderHtml();
  if (!html || typeof document === 'undefined') return null;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  for (const el of Array.from(tpl.content.children)) {
    if (el.tagName === 'SCRIPT' || /^Notifications/.test(el.getAttribute('aria-label') ?? '')) el.remove();
  }
  return tpl.content.childElementCount > 0 ? tpl.innerHTML : null;
}

/** Attributes that carry a tag's value rather than its identity. */
const NON_IDENTITY_ATTRS = new Set(['content', 'href', 'data-rh']);

/** `link[rel="alternate"][hreflang="pl"]`: the tag and every attribute that names what it is. */
function identitySelector(el: Element): string {
  let selector = el.tagName.toLowerCase();
  for (const { name, value } of Array.from(el.attributes)) {
    if (NON_IDENTITY_ATTRS.has(name)) continue;
    selector += `[${name}="${value.replace(/["\\]/g, '\\$&')}"]`;
  }
  return selector;
}

/**
 * Puts the server's head tags back beside the restored body.
 *
 * When the route chunk never loads, the route's Helmet never mounts and
 * Helmet's next commit removes every `[data-rh]` tag it does not own: the
 * canonical, the hreflang cluster and the description vanish while the body
 * stays (measured 2026-09-19 under Googlebot's UA; Google then reports
 * userCanonical=(none)). The copies go back without the marker, so Helmet
 * leaves them alone; a tag that is still present is not duplicated.
 * Returns the number of tags inserted.
 */
export function restorePrerenderHead(): number {
  if (!hasPrerenderForCurrentPath() || !snapshot || typeof document === 'undefined') return 0;
  let inserted = 0;
  try {
    const head = document.head;
    head.querySelectorAll('[data-rh]').forEach((el) => {
      if (el.tagName !== 'TITLE') el.remove();
    });
    const tpl = document.createElement('template');
    for (const html of snapshot.headTags) {
      tpl.innerHTML = html;
      const el = tpl.content.firstElementChild;
      if (!el) continue;
      el.removeAttribute('data-rh');
      const twins = Array.from(head.querySelectorAll(identitySelector(el)));
      if (twins.some((t) => t.textContent === el.textContent)) continue;
      head.appendChild(el);
      inserted++;
    }
    if (snapshot.title && document.title !== snapshot.title) document.title = snapshot.title;
  } catch {
    /* the body restore must not depend on the head restore */
  }
  return inserted;
}

/** Test-only: clears module state between cases. */
export function __resetPrerenderSnapshotForTests(): void {
  snapshot = null;
}
